import z from 'zod';
import { type Tool, tool } from '../../tool';
import {
  type Dir,
  dir,
  type Entry,
  type File,
  file,
  type GitRepo,
  type LocalDir,
  type LocalFile,
} from '../entries';
import { isDir, isDirectoryLikeEntry } from '../entries/guards';
import { SandboxSkillsConfigError } from '../errors';
import { Manifest, normalizeRelativePath } from '../manifest';
import { joinRelativePaths } from '../memory/storage';
import { prompt } from '../runtime/prompts';
import type { MaterializeEntryArgs, SandboxDirectoryEntry } from '../session';
import { jsonEqual } from '../shared/compare';
import { Capability, requireBoundSession } from './base';

const SKILLS_SECTION_INTRO = prompt`
技能是一组本地指令，保存在 \`SKILL.md\` 文件中。下面列出了当前可用的技能。每一项都包含名称、描述和文件路径，便于你在使用某个具体技能时打开其源文件，查看完整指令
`;

const HOW_TO_USE_SKILLS_SECTION = prompt`
### 如何使用技能
- 发现技能：上方列表展示了本会话中可用的技能，包括名称、描述和文件路径。技能的完整内容存放在对应路径的磁盘文件中
- 触发规则：如果用户点名某个技能（使用 \`$SkillName\` 或普通文本），或者当前任务明显匹配上方某个技能的描述，你必须在本轮使用该技能。若用户提到多个技能，则都需要使用。除非用户再次提到，否则不要将技能沿用到后续轮次
- 缺失或受阻：如果用户点名的技能不在列表中，或对应路径无法读取，请简要说明情况，并使用最佳替代方案继续完成任务
- 如何使用技能（按需展开）:
  1) 决定使用某个技能后，打开它的 \`SKILL.md\`。只读取足以执行其工作流的内容
  2) 如果 \`SKILL.md\` 指向了额外目录，例如 \`references/\`，只加载本次请求所需的具体文件；不要一次性批量加载所有内容
  3) 如果存在 \`scripts/\` 目录，优先运行或修改其中的脚本，而不是重新手写大段代码
  4) 如果存在 \`assets/\` 或模板，优先复用它们，而不是从零开始重新创建
- 协调与执行顺序：
  - 如果多个技能都适用，选择能覆盖请求的最小技能集合，并说明你将按什么顺序使用它们。
  - 简短说明你正在使用哪些技能以及原因（一句话即可）。如果跳过了一个明显相关的技能，也要说明原因。
- 上下文管理：
  - 保持上下文精简：对长段内容进行总结，而不是直接粘贴全文；只在需要时加载额外文件。
  - 避免过深地追踪引用：除非遇到阻塞，否则优先只打开 \`SKILL.md\` 中直接链接的文件。
  - 如果存在多个变体，例如框架、服务提供商或业务领域，只选择与当前任务相关的参考文件，并说明这一选择。
- 安全与兜底：如果某个技能无法顺利应用，例如文件缺失或指令不清，请说明问题，选择次优但可行的方案，并继续完成任务
`;

const HOW_TO_USE_LAZY_SKILLS_SECTION = prompt`
### 如何使用技能
- 发现技能：上方列表是本会话可用的技能索引，包括名称、描述和工作区路径。在按需加载模式下，这些路径不会一开始就存在，而是在需要时才加载。
- 触发规则：如果用户点名某个技能（使用 \`$SkillName\` 或普通文本），或者当前任务明显匹配上方某个技能的描述，你必须在本轮使用该技能。若用户提到多个技能，则都需要使用。除非用户再次提到，否则不要将技能沿用到后续轮次。
- 缺失或受阻：如果用户点名的技能不在列表中，或对应路径无法读取，请简要说明情况，并使用最佳替代方案继续完成任务。
- 如何使用技能（按需展开）：
  1) 决定使用某个按需加载的技能后，先针对该技能调用 \`load_skill\`，然后再打开它的 \`SKILL.md\`。
  2) 如果 \`SKILL.md\` 指向了额外目录，例如 \`references/\`，只加载本次请求所需的具体文件；不要一次性批量加载所有内容。
  3) 如果存在 \`scripts/\` 目录，优先运行或修改其中的脚本，而不是重新手写大段代码。
  4) 如果存在 \`assets/\` 或模板，优先复用它们，而不是从零开始重新创建。
- 协调与执行顺序：
  - 如果多个技能都适用，选择能覆盖请求的最小技能集合，并说明你将按什么顺序使用它们。
  - 简短说明你正在使用哪些技能以及原因（一句话即可）。如果跳过了一个明显相关的技能，也要说明原因。
- 上下文管理：
  - 保持上下文精简：对长段内容进行总结，而不是直接粘贴全文；只在需要时加载额外文件。
  - 避免过深地追踪引用：除非遇到阻塞，否则优先只打开 \`SKILL.md\` 中直接链接的文件。
  - 如果存在多个变体，例如框架、服务提供商或业务领域，只选择与当前任务相关的参考文件，并说明这一选择。
- 安全与兜底：如果某个技能无法顺利应用，例如文件缺失或指令不清，请说明问题，选择次优但可行的方案，并继续完成任务。
`;

export type SkillIndexEntry = {
  name: string;
  description: string;
  path?: string;
};

export type LocalDirLazySkillSource = {
  source: Dir | LocalDir | GitRepo;
  index?: SkillIndexEntry[];
};

export type SkillDescriptor = {
  name: string;
  description: string;
  content: string | Uint8Array | File | LocalFile;
  scripts?: Record<string, Entry>;
  references?: Record<string, Entry>;
  assets?: Record<string, Entry>;
  compatibility?: string[];
  deferred?: boolean;
};
export type SkillsArgs = {
  skills?: SkillDescriptor[];
  from?: Entry;
  lazyFrom?: LocalDirLazySkillSource;
  index?: SkillIndexEntry[];
  skillsPath?: string;
};

class SkillsCapability extends Capability {
  readonly type = 'skills';
  readonly skills: SkillDescriptor[];
  readonly from?: Entry;
  readonly lazyFrom?: LocalDirLazySkillSource;
  readonly index?: SkillIndexEntry[];
  readonly skillsPath: string;

  constructor(args: SkillsArgs) {
    super();
    this.skills = args.skills ?? [];
    this.from = args.from;
    this.lazyFrom = args.lazyFrom;
    this.index = args.index;
    this.skillsPath = normalizeRelativePath(args.skillsPath ?? '.agents');

    validateSkillsCapability(this);
  }

  override tools(): Tool<any>[] {
    if (!this.lazyFrom) {
      return [];
    }

    const session = requireBoundSession(this.type, this._session);
    if (!session.pathExists || !session.materializeEntry) {
      throw new SandboxSkillsConfigError(
        'Skills sandbox sessions must provide pathExists() and materializeEntry().',
      );
    }

    return [
      tool({
        name: 'load_skill',
        description:
          'Load a single lazily configured skill into the sandbox so its SKILL.md, scripts, references, and assets can be read from the workspace.',
        parameters: z.object({
          skill_name: z
            .string()
            .min(1)
            .describe('Name of the lazily configured skill to materialize.'),
        }),
        execute: async ({
          skill_name,
        }: {
          skill_name: string;
        }): Promise<{
          status: 'loaded' | 'already_loaded';
          skill_name: string;
          path: string;
        }> => {
          const match = resolveLazySkillMatch(this, skill_name);
          const relativeSkillPath = normalizeRelativePath(
            match.path ?? match.name,
          );
          const destinationPath = joinRelativePaths(
            this.skillsPath,
            relativeSkillPath,
          );
          const skillMarkdownPath = joinRelativePaths(
            destinationPath,
            'SKILL.md',
          );

          if (await session.pathExists!(skillMarkdownPath, this._runAs)) {
            return {
              status: 'already_loaded',
              skill_name: match.name,
              path: destinationPath,
            };
          }

          await session.materializeEntry!({
            path: destinationPath,
            entry: resolveLazySkillEntry(
              this.lazyFrom!.source,
              relativeSkillPath,
            ),
            runAs: this._runAs,
          } satisfies MaterializeEntryArgs);

          return {
            status: 'loaded',
            skill_name: match.name,
            path: destinationPath,
          };
        },
      }),
    ];
  }

  override processManifest(manifest: Manifest): Manifest {
    const existingPaths = new Set(
      Object.keys(manifest.entries).map((path) => normalizeRelativePath(path)),
    );

    if (this.lazyFrom) {
      const loadedLazySkillEntries = resolveLoadedLazySkillEntries(this);
      const overlaps = [...existingPaths]
        .filter((path) => {
          if (!pathsOverlap(path, this.skillsPath)) {
            return false;
          }
          const expectedEntry = loadedLazySkillEntries.get(path);
          if (
            expectedEntry &&
            jsonEqual(manifest.entries[path], expectedEntry)
          ) {
            return false;
          }
          return true;
        })
        .sort();
      if (overlaps.length > 0) {
        throw new SandboxSkillsConfigError(
          `skills lazyFrom path overlaps existing manifest entries: ${overlaps.join(', ')}`,
        );
      }
      return manifest;
    }

    if (this.from) {
      const from = this.from;
      const overlap = [...existingPaths].find((path) =>
        isConflictingManifestOverlap(manifest, path, this.skillsPath, from),
      );
      if (overlap) {
        throw new SandboxSkillsConfigError(
          `skills path overlaps existing manifest entries: ${overlap}`,
        );
      }
      manifest.entries[this.skillsPath] = from;
      return manifest;
    }

    for (const skill of this.skills) {
      const skillPath = joinRelativePaths(this.skillsPath, skill.name);
      const skillEntry = renderSkillDescriptor(skill);
      const overlap = [...existingPaths].find((path) =>
        isConflictingManifestOverlap(manifest, path, skillPath, skillEntry),
      );
      if (overlap) {
        throw new SandboxSkillsConfigError(
          `skill path overlaps existing manifest entries: ${skillPath}`,
        );
      }
      manifest.entries[skillPath] = skillEntry;
      existingPaths.add(skillPath);
    }

    return manifest;
  }

  override async instructions(manifest: Manifest): Promise<string | null> {
    const metadata = resolveSkillsMetadata(
      this,
      await this.resolveRuntimeMetadata(manifest),
    );
    if (metadata.length === 0 && this.from) {
      return renderSkillsDiscoveryInstructions(this.skillsPath);
    }
    if (metadata.length === 0) return null;

    return renderSkillsInstructions({
      metadata,
      lazy: Boolean(this.lazyFrom),
    });
  }

  private async resolveRuntimeMetadata(
    _manifest: Manifest,
  ): Promise<SkillIndexEntry[]> {
    if (!this.from || !this._session?.listDir || !this._session.readFile) {
      return [];
    }

    let entries: SandboxDirectoryEntry[];
    try {
      entries = await this._session.listDir({
        path: this.skillsPath,
        runAs: this._runAs,
      });
    } catch {
      return [];
    }

    const metadata: SkillIndexEntry[] = [];
    for (const entry of entries) {
      if (entry.type !== 'dir') {
        continue;
      }

      let content: string | Uint8Array;
      try {
        content = await this._session.readFile({
          path: joinRelativePaths(entry.path, 'SKILL.md'),
          runAs: this._runAs,
        });
      } catch {
        continue;
      }

      const markdown =
        typeof content === 'string'
          ? content
          : new TextDecoder().decode(content);
      const frontmatter = parseSkillFrontmatter(markdown);
      metadata.push({
        name: frontmatter.name ?? entry.name,
        description: frontmatter.description ?? 'No description provided.',
        path: entry.name,
      });
    }

    return metadata;
  }
}

export type Skills = SkillsCapability;
export function skills(args: SkillsArgs): Skills;
export function skills(args: SkillsArgs): Skills {
  return new SkillsCapability(args);
}

function validateSkillsCapability(capability: SkillsCapability): void {
  const configuredSources = [
    capability.skills.length > 0,
    Boolean(capability.from),
    Boolean(capability.lazyFrom),
  ].filter(Boolean).length;

  if (configuredSources === 0) {
    throw new SandboxSkillsConfigError(
      'skills capability requires `skills`, `from`, or `lazyFrom`.',
    );
  }
  if (configuredSources > 1) {
    throw new SandboxSkillsConfigError(
      'skills capability accepts only one of `skills`, `from`, or `lazyFrom`.',
    );
  }
  if (capability.from && !isDirectoryLikeEntry(capability.from)) {
    throw new SandboxSkillsConfigError(
      'skills from must be a directory-like entry such as dir, git_repo, or mount.',
    );
  }

  const seenSkillNames = new Set<string>();
  for (const skill of capability.skills) {
    const normalizedName = normalizeRelativePath(skill.name);
    if (seenSkillNames.has(normalizedName)) {
      throw new SandboxSkillsConfigError(`duplicate skill name: ${skill.name}`);
    }
    seenSkillNames.add(normalizedName);
  }
}

function resolveLazySkillMatch(
  capability: SkillsCapability,
  skillName: string,
): SkillIndexEntry {
  const index =
    resolveLazySkillIndex(capability.lazyFrom) ?? capability.index ?? [];
  const matches = index.filter((skill) => {
    const relativePath = normalizeRelativePath(skill.path ?? skill.name);
    const pathBaseName = relativePath.split('/').pop() ?? relativePath;
    return (
      skill.name === skillName ||
      relativePath === skillName ||
      pathBaseName === skillName
    );
  });

  if (matches.length === 0) {
    throw new SandboxSkillsConfigError(`lazy skill not found: ${skillName}`);
  }
  if (matches.length > 1) {
    throw new SandboxSkillsConfigError(
      `lazy skill name is ambiguous: ${skillName}`,
    );
  }

  return matches[0];
}

function resolveLazySkillIndex(
  lazySource: LocalDirLazySkillSource | undefined,
): SkillIndexEntry[] | undefined {
  if (!lazySource) {
    return undefined;
  }
  if (lazySource.index) {
    return lazySource.index;
  }

  const derivedIndex = deriveIndexFromDirEntry(lazySource.source);
  return derivedIndex.length > 0 ? derivedIndex : undefined;
}

function resolveLoadedLazySkillEntries(
  capability: SkillsCapability,
): Map<string, Entry> {
  const loadedEntries = new Map<string, Entry>();
  if (!capability.lazyFrom) {
    return loadedEntries;
  }

  const index =
    resolveLazySkillIndex(capability.lazyFrom) ?? capability.index ?? [];
  for (const skill of index) {
    const relativeSkillPath = normalizeRelativePath(skill.path ?? skill.name);
    const destinationPath = joinRelativePaths(
      capability.skillsPath,
      relativeSkillPath,
    );
    try {
      loadedEntries.set(
        destinationPath,
        resolveLazySkillEntry(capability.lazyFrom.source, relativeSkillPath),
      );
    } catch {
      // Keep validation strict if the configured index cannot resolve a source entry.
    }
  }

  return loadedEntries;
}

function deriveIndexFromDirEntry(entry: Entry | undefined): SkillIndexEntry[] {
  if (!entry || !isDir(entry) || !entry.children) {
    return [];
  }

  return Object.entries(entry.children)
    .filter(([, child]) => child.type === 'dir')
    .map(([name, child]) => ({
      name,
      description: child.description ?? 'No description provided.',
    }));
}

function resolveLazySkillEntry(
  source: LocalDirLazySkillSource['source'],
  relativeSkillPath: string,
): Entry {
  if (source.type === 'local_dir') {
    if (!source.src) {
      throw new SandboxSkillsConfigError(
        'Lazy skill local_dir sources require a concrete src value.',
      );
    }
    return {
      ...source,
      type: 'local_dir',
      src: joinSourcePath(source.src, relativeSkillPath),
    };
  }

  if (source.type === 'git_repo') {
    const baseSubpath = source.subpath
      ? normalizeRelativePath(source.subpath)
      : '';
    return {
      ...source,
      type: 'git_repo',
      subpath: joinRelativePaths(baseSubpath, relativeSkillPath),
    };
  }

  if (source.type === 'dir') {
    const child = readDirChild(source, relativeSkillPath);
    if (!child) {
      throw new SandboxSkillsConfigError(
        `lazy skill source path not found: ${relativeSkillPath}`,
      );
    }
    return child;
  }

  throw new SandboxSkillsConfigError(
    `Lazy skill source type is not supported yet: ${(source as Entry).type}`,
  );
}

function joinSourcePath(sourceRoot: string, relativePath: string): string {
  const trimmedRoot = sourceRoot.replace(/[\\/]+$/, '');
  if (!trimmedRoot) {
    return relativePath;
  }

  return `${trimmedRoot}/${relativePath}`;
}

function pathsOverlap(left: string, right: string): boolean {
  return (
    left === right ||
    left.startsWith(`${right}/`) ||
    right.startsWith(`${left}/`)
  );
}

function isConflictingManifestOverlap(
  manifest: Manifest,
  existingPath: string,
  targetPath: string,
  targetEntry: Entry,
): boolean {
  if (!pathsOverlap(existingPath, targetPath)) {
    return false;
  }
  return !(
    existingPath === targetPath &&
    jsonEqual(manifest.entries[existingPath], targetEntry)
  );
}

function renderSkillDescriptor(skill: SkillDescriptor): Dir {
  const children: Record<string, Entry> = {
    'SKILL.md': normalizeSkillContent(skill.content),
  };

  if (skill.scripts && Object.keys(skill.scripts).length > 0) {
    children.scripts = dir({
      children: skill.scripts,
    });
  }
  if (skill.references && Object.keys(skill.references).length > 0) {
    children.references = dir({
      children: skill.references,
    });
  }
  if (skill.assets && Object.keys(skill.assets).length > 0) {
    children.assets = dir({
      children: skill.assets,
    });
  }

  return dir({
    children,
  });
}

function normalizeSkillContent(
  content: SkillDescriptor['content'],
): File | LocalFile {
  if (typeof content === 'string' || content instanceof Uint8Array) {
    return file({
      content,
    });
  }

  return content;
}

function resolveSkillsMetadata(
  capability: SkillsCapability,
  runtimeMetadata: SkillIndexEntry[] = [],
): SkillIndexEntry[] {
  if (capability.skills.length > 0) {
    return capability.skills
      .map((skill) => ({
        name: skill.name,
        description: skill.description,
        path: joinRelativePaths(capability.skillsPath, skill.name),
      }))
      .sort((left, right) => left.name.localeCompare(right.name));
  }

  const configuredIndex =
    resolveLazySkillIndex(capability.lazyFrom) ??
    capability.index ??
    (runtimeMetadata.length > 0 ? runtimeMetadata : undefined) ??
    deriveIndexFromDirEntry(capability.from);

  return configuredIndex
    .map((skill) => ({
      ...skill,
      path: joinRelativePaths(
        capability.skillsPath,
        skill.path ?? normalizeRelativePath(skill.name),
      ),
    }))
    .sort((left, right) => left.name.localeCompare(right.name));
}

function renderSkillsDiscoveryInstructions(skillsPath: string): string {
  return prompt`
## Skills
${SKILLS_SECTION_INTRO}

### Skill 来源
- 技能会被实际落盘到 ${skillsPath} 下。请在运行时检查其子目录以及其中的 SKILL.md 文件，以发现当前可用的技能

${HOW_TO_USE_SKILLS_SECTION}
`;
}

function renderSkillsInstructions(args: {
  metadata: SkillIndexEntry[];
  lazy: boolean;
}): string {
  const availableSkills = args.metadata
    .map(
      (skill) =>
        `- ${skill.name}: ${skill.description} (file: ${skill.path ?? skill.name}/SKILL.md)`,
    )
    .join('\n');
  const lazyLoadingInstructions = args.lazy
    ? prompt`
### 按需加载
- 这些技能已建立索引，可用于任务规划，但尚未实际展开到工作区中
- 在读取某个技能的 \`SKILL.md\` 或工作区中的其他相关文件之前，必须先使用列表中的单个技能名称调用 \`load_skill\`
- 每次调用 \`load_skill\` 只会将一个技能加载到列表所示路径下。如果需要使用多个技能，请多次调用
`
    : '';
  const usageInstructions = args.lazy
    ? HOW_TO_USE_LAZY_SKILLS_SECTION
    : HOW_TO_USE_SKILLS_SECTION;
  const lazyLoadingSection = lazyLoadingInstructions
    ? `${lazyLoadingInstructions}\n\n`
    : '';

  return prompt`
## 技能
${SKILLS_SECTION_INTRO}

### 可用技能
${availableSkills}

${lazyLoadingSection}${usageInstructions}
`;
}

export function parseSkillFrontmatter(
  markdown: string,
): Record<string, string> {
  const lines = markdown.split(/\r?\n/);
  if (lines[0]?.trim() !== '---') {
    return {};
  }

  const endIndex = lines.findIndex(
    (line, index) => index > 0 && line.trim() === '---',
  );
  if (endIndex === -1) {
    return {};
  }

  const metadata: Record<string, string> = {};
  for (const line of lines.slice(1, endIndex)) {
    const stripped = line.trim();
    const delimiterIndex = stripped.indexOf(':');
    if (!stripped || stripped.startsWith('#') || delimiterIndex === -1) {
      continue;
    }

    const key = stripped.slice(0, delimiterIndex).trim();
    const rawValue = stripped.slice(delimiterIndex + 1).trim();
    if (!key) {
      continue;
    }
    metadata[key] = unquoteFrontmatterValue(rawValue);
  }

  return metadata;
}

function unquoteFrontmatterValue(value: string): string {
  if (
    value.length >= 2 &&
    value[0] === value[value.length - 1] &&
    (value[0] === '"' || value[0] === "'")
  ) {
    return value.slice(1, -1);
  }
  return value;
}

function readDirChild(
  source: Dir,
  relativeSkillPath: string,
): Entry | undefined {
  let current: Entry | undefined = source;
  for (const segment of normalizeRelativePath(relativeSkillPath).split('/')) {
    if (!segment) {
      continue;
    }
    if (!current || !isDir(current) || !current.children) {
      return undefined;
    }
    current = current.children[segment];
  }
  return current;
}
